/**
 * Telegram Mini App Utilities
 * Wraps @twa-dev/sdk for safer usage and consistency.
 */

import WebApp from '@twa-dev/sdk';

// Get the actual WebApp object from the SDK or fallback to the global window.Telegram.WebApp
// This handles different build environment behaviors (CJS vs ESM)
const tg = (WebApp as any)?.default || WebApp || (window as any).Telegram?.WebApp;

export const tma = {
  init() {
    if (tg?.ready) {
      tg.ready();
    }
    if (tg?.expand) {
      tg.expand();
    }
    
    // Set theme colors based on Telegram values
    if (tg) {
      document.documentElement.style.setProperty('--tg-theme-bg', tg.backgroundColor || '#0f172a');
      document.documentElement.style.setProperty('--tg-theme-text', tg.textColor || '#ffffff');
    }
  },

  getUser() {
    let user = tg?.initDataUnsafe?.user || null;
    if (!user) {
      // Running in development browser preview
      const activeRole = localStorage.getItem('nexus_preview_role') || 'user';
      if (activeRole === 'super_admin') {
        user = {
          id: 7804637246,
          first_name: 'Admin',
          username: 'MasterAdmin',
          display_name: 'Super Admin (7804637246)'
        };
      } else {
        try {
          const cached = localStorage.getItem('nexus_preview_user_v2');
          if (cached) {
            user = JSON.parse(cached);
          } else {
            const randomId = Math.floor(20000000 + Math.random() * 70000000); // Unique 8-digit separate tester ID
            user = {
              id: randomId,
              first_name: `Tester_${randomId.toString().slice(-4)}`,
              username: `Tester_${randomId.toString().slice(-4)}`,
              display_name: `Tester ${randomId.toString().slice(-4)}`
            };
            localStorage.setItem('nexus_preview_user_v2', JSON.stringify(user));
          }
        } catch (e) {
          user = { id: 87654321, first_name: 'FallbackTester', display_name: 'Tester' };
        }
      }
    }
    if (user) {
      // Add computed username property if not present
      user.display_name = user.username || user.first_name || 'Anonymous';
    }
    return user;
  },

  getInitData() {
    return tg?.initData || '';
  },

  getStartParam() {
    return tg?.initDataUnsafe?.start_param || '';
  },

  isExpanded() {
    return tg?.isExpanded || false;
  },

  hapticFeedback(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' | 'success' | 'warning' | 'error' = 'light') {
    if (!tg?.HapticFeedback) return;
    
    if (['success', 'warning', 'error'].includes(style)) {
      tg.HapticFeedback.notificationOccurred(style as 'success' | 'warning' | 'error');
    } else {
      tg.HapticFeedback.impactOccurred(style as 'light' | 'medium' | 'heavy' | 'rigid' | 'soft');
    }
  },

  shareLink(url: string, text: string = '') {
    if (tg?.openTelegramLink) {
      const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
      tg.openTelegramLink(shareUrl);
    } else {
      navigator.clipboard.writeText(url);
    }
  },

  openLink(url: string) {
    if (tg?.openLink) {
      tg.openLink(url);
    } else {
      window.open(url, '_blank');
    }
  },

  close() {
    if (tg?.close) {
      tg.close();
    }
  },

  showBackButton(onClickCallback?: () => void) {
    if (tg?.BackButton) {
      tg.BackButton.show();
      if (onClickCallback) {
        tg.BackButton.offClick(); // remove old callbacks
        tg.BackButton.onClick(onClickCallback);
      }
    }
  },

  hideBackButton() {
    if (tg?.BackButton) {
      tg.BackButton.hide();
      tg.BackButton.offClick();
    }
  },

  showAlert(message: string) {
    if (tg?.showAlert) {
      tg.showAlert(message);
    } else {
      alert(message);
    }
  }
};
